import {useState, type FunctionComponent} from "react";
import {useEscrowStore} from "@stores/escrow";
import {usePointsStore} from "@stores/points";
import {useLegalStore} from "@stores/legal";
import {SelectableAssetDeposit} from "@components";
import {Button} from "@bracket/react-button";
import {useMessengerStore} from "@bracket/react-messenger";
import {bi, sn, getBlockLink} from "@helpers";
import {Loader} from "@bracket/react-loader";
import {useModalizeStore} from "@components";
import {TermsAndServices, PrivacyPolicy, LegalAgreements} from "@sections";

import "./SetStake.scss";

import type {TokenDefinition} from "@bracket/addressbook";
import {useChainId} from "wagmi";

export const SetStake: FunctionComponent = () => {

	const chainId = useChainId();
	const {sendMessage} = useMessengerStore();
	const {modalize, dismiss} = useModalizeStore();
	const [showInvalid, setShowInvalid] = useState(false);

	const {
		busy,
		depositAmountValid,
		depositAmountMessage,
		initialized,
		depositAmount,
		depositToken,
		tokens,
		applyMaxDeposit,
		deposit,
		getTokenAvailable,
		setDepositToken,
		setDepositAmount,
		toUSD,
		reset,
	} = useEscrowStore();

	const {
		shouldSign,
		sign,
	} = usePointsStore();

	const {accepted_terms, setTermsAccepted} = useLegalStore();

	const classes = () => ["section-set-stake"].join(" ");

	if (!depositToken) {
		throw new Error(
			"@bracket/stake::SetStakeNext cannot be called without a valid deposit token set",
		);
	}

	const balance = getTokenAvailable(depositToken);
	const depositInUSD = toUSD(depositToken, bi(sn(depositAmount)));
	const onAmountChange = (amount: string) => setDepositAmount(amount);
	const onTokenChange = (token: TokenDefinition) => setDepositToken(token);
	const onBlur = () => setShowInvalid(true);
	const onFocus = () => setShowInvalid(false);
	const disabled = showInvalid && !depositAmountValid;
	const label = () =>
		showInvalid && !depositAmountValid ? depositAmountMessage : "Stake";

	const onTermsAndServices = () => {
		modalize({
			component: TermsAndServices,
			title: "Terms of service",
			blur: true,
		});
	};

	const onPrivacyPolicy = () => {
		modalize({component: PrivacyPolicy, title: "Privacy Policy", blur: true});
	};

	const revealTerms = () => {
		const buttons: any[] = [
			{
				button: Button,
				buttonProps: {
					label: "Close",
					onClick: onReject,
					variant: "outline",
					flexible: true,
				},
			},
			{
				button: Button,
				buttonProps: {
					label: "I accept	",
					onClick: onAcceptAgreements,
					variant: "outline",
					flexible: true,
				},
			},
		];
		modalize({
			component: LegalAgreements,
			title: "Terms of service / Privacy Policy",
			subtitle:
				"You must read the document in it's entirety to continue (scroll)",
			blur: true,
			buttons,
		});
	};

	// const onAcceptTerms = () => {
	// 	dismiss();
	// 	const buttons: any[] = [
	// 		{
	// 			button: Button,
	// 			buttonProps: {
	// 				label: "Close",
	// 				onClick: onReject,
	// 				variant: "outline",
	// 				flexible: true,
	// 			},
	// 		},
	// 		{
	// 			button: Button,
	// 			buttonProps: {
	// 				label: "I accept	",
	// 				onClick: onAcceptPolicy,
	// 				variant: "outline",
	// 				flexible: true,
	// 			},
	// 		},
	// 	];
	// 	modalize({
	// 		component: PrivacyPolicy,
	// 		title: "Privacy Policy",
	// 		subtitle:
	// 			"You must read the document in it's entirety to continue (scroll)",
	// 		blur: true,
	// 		buttons,
	// 	});
	// };

	// const onAcceptPolicy = () => {
	// 	setTermsAccepted(true);
	// 	dismiss();
	// 	onStake(true);
	// };

	const onAcceptAgreements = () => {
		setTermsAccepted(true);
		dismiss();
		onStake(true);
	};

	const onReject = () => {
		dismiss();
	};

	const onStake = (force = false) =>
		(async () => {
			if (accepted_terms || force === true) {
				const [signage, user_message] = shouldSign();
				const signer = signage ? sign : null;
				const response = await deposit(signer);
				const { code, status: severity, message, } = response;
				switch (code) {
					case "SUCCESS":
						sendMessage({
							title: "Stake",
							severity,
							message,
							blockChainLink: getBlockLink(response.hash),
						});
						if(user_message) {
							sendMessage({ severity: "warning", message: user_message, title: "Referral information:" });
						}
						window.history.pushState({}, "", "/stake/balances");
						reset();
						return;

					case "CANCELLED":
					case "UNDERFUND":
					case "UNHANDLED":
						sendMessage({title: "Stake", severity, message});
						reset();
						return;

					default:
						throw new Error(`StakeNext::unhandled response code "${code}"`);
				}
			} else {
				revealTerms();
			}
		})();

	return (
		<div className={classes()}>
			<SelectableAssetDeposit
				amount={depositAmount}
				amountValid={depositAmountValid}
				amountInvalidReason={depositAmountMessage}
				applyMax={applyMaxDeposit}
				balance={balance}
				balanceUSD={depositInUSD}
				chain={chainId}
				interactive={!busy}
				onTokenChange={onTokenChange}
				onValueChange={onAmountChange}
				token={depositToken}
				tokens={tokens}
				showBalance
				onBlur={onBlur}
				onFocus={onFocus}
				hackvar="setd"
			/>
			<Button
				label={label()}
				onClick={onStake}
				busy={busy}
				disabled={disabled}
			/>
			<div className="disclaimer">
				<p>
					By using Bracket services, you agree to the{" "}
					<a href="#" onClick={onTermsAndServices}>
						Terms of Service
					</a>{" "}
					and consent to our{" "}
					<a href="#" onClick={onPrivacyPolicy}>
						Privacy Policy
					</a>
					.
				</p>
				<br />
				<p>You can withdraw at any time and keep the [BARS] you have earned.</p>
			</div>
		</div>
	);
};
